/**
 * 大海龟旅店 — 开发期占位纹理（DEV ONLY，永不随正式版发布）。
 * 每个纹理都画在它的正式资源 key 下（`inn-tiles`、`inn-column`、`emote-laugh`…），且只在该 key 尚不存在时才画：
 * 正式资源经 manifest 先行加载后，这里自动成为空操作。纹理里不烙任何中文；调色板取自 ART_DIRECTION.md。
 * 全部绘制确定性（散点用 SeededRng）。
 */
import type Phaser from 'phaser';
import { TILE } from '@/core/config';
import { SeededRng } from '@/core/rng';
import { ACTOR_DEFS } from '@/characters/actorDefs';
import { FACINGS, forgetActorAnims, frameName } from '@/characters/anims';
import { INN_PROP, INN_TILE, INN_TILESET } from './innMap';
import { EMOTE_ICON_ALIAS } from './innPoses';

type Ctx = CanvasRenderingContext2D;
const P = {
  sand: '#d8b982', sandDark: '#b8955f', sandSpeck: '#c9a86e', mud: '#9c7a4a', puddle: '#6f8fa8', puddleHi: '#a9c4d6',
  path: '#b08a5a', pathDark: '#8a5a32', stone: '#8c8474', stoneDark: '#7a7262', grout: '#6e6656',
  plank: '#b07a3c', plankDark: '#8a5a32', wood: '#6e4524', woodDark: '#5a3a1e', lacquer: '#8c2a22', cloth: '#e2c48c',
  shell: '#6b5a3a', shellLine: '#4f4229', shellFace: '#4a3f2a', shellRim: '#7d6a45', shellShadow: '#2a2318',
  straw: '#c9b89a', strawDark: '#b8a480', indigo: '#2b3a5c', storm: '#3a3f57', lantern: '#ffb347', lanternHi: '#ffe0a0',
  turtle: '#3c7a3a', turtleHi: '#5a8a4a', turtleLine: '#2a5a2a', turtleSkin: '#8ec95a', stopper: '#e2c48c',
  bubble: '#fff4d6', ink: '#2a1c14', red: '#8c2a22', blush: '#e07070', teal: '#1f8f8a', grey: '#6a6a72',
};

function rect(c: Ctx, x: number, y: number, w: number, h: number, color: string): void { c.fillStyle = color; c.fillRect(x, y, w, h); }

/**
 * 画完再注册：WebGL 在 addCanvas 时上传画布，之后再画的内容不会进显存（要么先画完，要么 refresh）。
 * 返回 false 表示该 key 已存在（正式资源已到货）或环境不支持。
 */
function withCanvas(scene: Phaser.Scene, key: string, w: number, h: number, draw: (c: Ctx) => void): boolean {
  if (scene.textures.exists(key)) return false;
  const canvas = document.createElement('canvas');
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) { console.warn(`[turtle-inn] 2D context unavailable; dev texture ${key} skipped`); return false; }
  ctx.imageSmoothingEnabled = false;
  draw(ctx);
  const tex = scene.textures.addCanvas(key, canvas);
  if (!tex) { console.warn(`[turtle-inn] could not register dev texture ${key}`); return false; }
  return true;
}

// ------------------------------------------------------------------ tiles
function drawTile(c: Ctx, id: number, ox: number, rng: SeededRng): void {
  const T = INN_TILE;
  const r = (x: number, y: number, w: number, h: number, col: string) => rect(c, ox + x, y, w, h, col);
  switch (id) {
    case T.SAND:
      r(0, 0, TILE, TILE, P.sand);
      for (let i = 0; i < 12; i++) r(rng.int(0, 31), rng.int(0, 31), 1, 1, P.sandSpeck);
      break;
    case T.SAND_DARK:
      r(0, 0, TILE, TILE, P.sandDark);
      for (let i = 0; i < 10; i++) r(rng.int(0, 30), rng.int(0, 31), 2, 1, P.mud);
      break;
    case T.PUDDLE:
      r(0, 0, TILE, TILE, P.sand);
      for (let i = 0; i < 6; i++) r(rng.int(0, 31), rng.int(0, 31), 1, 1, P.sandSpeck);
      r(6, 10, 20, 12, P.puddle); r(4, 12, 24, 8, P.puddle); r(8, 8, 16, 2, P.puddle); r(8, 22, 16, 2, P.puddle);
      r(9, 12, 6, 1, P.puddleHi); r(18, 17, 5, 1, P.puddleHi);
      break;
    case T.PATH:
      r(0, 0, TILE, TILE, P.path);
      for (let i = 0; i < 8; i++) r(rng.int(0, 28), rng.int(0, 31), 3, 1, P.pathDark);
      break;
    case T.FLOOR:
    case T.FLOOR_DARK: {
      const base = id === T.FLOOR ? P.stone : P.stoneDark;
      r(0, 0, TILE, TILE, base);
      r(0, 15, TILE, 1, P.grout); r(15, 0, 1, 16, P.grout); r(7, 16, 1, 16, P.grout); r(23, 16, 1, 16, P.grout);
      r(0, 31, TILE, 1, P.grout); r(31, 0, 1, TILE, P.grout);
      for (let i = 0; i < 5; i++) r(rng.int(1, 29), rng.int(1, 29), 2, 1, id === T.FLOOR ? '#9a9282' : '#847c6c');
      break;
    }
    case T.PLANK:
      r(0, 0, TILE, TILE, P.plank);
      for (let y = 0; y < TILE; y += 8) r(0, y, TILE, 1, P.wood);
      r(15, 0, 1, 8, P.plankDark); r(7, 8, 1, 8, P.plankDark); r(23, 16, 1, 8, P.plankDark); r(11, 24, 1, 8, P.plankDark);
      break;
    case T.RAIL:
      r(0, 0, TILE, TILE, P.plank);
      for (let y = 0; y < TILE; y += 8) r(0, y, TILE, 1, P.wood);
      r(0, 12, TILE, 3, P.woodDark); r(0, 22, TILE, 3, P.woodDark);
      r(2, 8, 4, 22, P.woodDark); r(26, 8, 4, 22, P.woodDark); r(14, 8, 4, 22, P.woodDark);
      r(0, 30, TILE, 2, P.shellShadow);
      break;
    case T.COUNTER_TOP:
      r(0, 0, TILE, 20, P.plank); r(0, 0, TILE, 2, P.lacquer); r(0, 18, TILE, 2, P.lacquer);
      r(0, 20, TILE, 12, P.wood); r(0, 30, TILE, 2, P.woodDark);
      r(4, 6, 6, 5, P.cloth); r(20, 5, 5, 6, P.plankDark); // 杯、壶
      break;
    case T.COUNTER_FRONT:
      r(0, 0, TILE, TILE, P.wood); r(0, 0, TILE, 2, P.lacquer); for (let y = 6; y < TILE; y += 8) r(0, y, TILE, 1, P.woodDark);
      break;
    case T.SHELF:
      r(0, 0, TILE, TILE, '#3a2a1e');
      r(0, 14, TILE, 2, P.woodDark); r(0, 30, TILE, 2, P.woodDark);
      for (const x of [2, 12, 22]) { r(x, 4, 8, 10, P.plankDark); r(x + 2, 2, 4, 3, P.stopper); r(x, 18, 8, 12, P.plankDark); r(x + 2, 16, 4, 3, P.stopper); }
      break;
    case T.STAIR:
      r(0, 0, TILE, TILE, P.plankDark);
      for (let y = 0; y < TILE; y += 8) { r(0, y, TILE, 5, P.plank); r(0, y + 5, TILE, 1, P.woodDark); }
      r(0, 0, 3, TILE, P.woodDark); r(29, 0, 3, TILE, P.woodDark);
      break;
    case T.DOOR:
      r(0, 0, TILE, TILE, P.plank);
      for (let y = 0; y < TILE; y += 8) r(0, y, TILE, 1, P.wood);
      r(6, 2, 20, 30, P.woodDark); r(8, 4, 16, 26, P.lacquer); r(14, 16, 4, 2, P.cloth);
      break;
    case T.RUG:
      r(0, 0, TILE, TILE, P.straw);
      for (let y = 0; y < TILE; y += 4) for (let x = ((y / 4) % 2) * 2; x < TILE; x += 4) r(x, y, 2, 1, P.strawDark);
      break;
    case T.EAVE:
      r(0, 0, TILE, TILE, P.shell);
      r(0, 0, TILE, 1, P.shellRim); r(0, 31, TILE, 1, P.shellLine);
      // 六角龟甲纹（两块）
      r(4, 6, 10, 1, P.shellLine); r(2, 7, 1, 8, P.shellLine); r(15, 7, 1, 8, P.shellLine); r(4, 15, 10, 1, P.shellLine);
      r(18, 16, 10, 1, P.shellLine); r(16, 17, 1, 8, P.shellLine); r(29, 17, 1, 8, P.shellLine); r(18, 25, 10, 1, P.shellLine);
      r(7, 9, 3, 1, P.puddleHi); r(21, 19, 3, 1, P.puddleHi); // 雨水反光
      break;
    case T.EAVE_FRONT:
      r(0, 0, TILE, 10, P.shell); r(0, 0, TILE, 1, P.shellRim); r(4, 3, 8, 1, P.shellLine); r(18, 5, 8, 1, P.shellLine);
      r(0, 10, TILE, 18, P.shellFace); r(0, 10, TILE, 2, P.shellRim); r(0, 26, TILE, 6, P.shellShadow);
      for (let i = 0; i < 4; i++) r(rng.int(1, 30), 12 + rng.int(0, 10), 1, 4, '#5e5238');
      break;
    case T.EAVE_GAP:
      r(0, 0, TILE, 8, P.shell); r(0, 0, TILE, 1, P.shellRim); r(0, 8, TILE, 2, P.shellFace); r(0, 10, TILE, 2, 'rgba(42,35,24,0.45)');
      r(6, 3, 8, 1, P.shellLine); r(20, 4, 6, 1, P.shellLine);
      break;
    case T.EAVE_CORNER:
      r(0, 0, TILE, TILE, P.shell); r(0, 0, TILE, 1, P.shellRim); r(0, 31, TILE, 1, P.shellLine); r(0, 0, 1, TILE, P.shellRim); r(31, 0, 1, TILE, P.shellLine);
      r(8, 8, 16, 1, P.shellLine); r(6, 9, 1, 14, P.shellLine); r(25, 9, 1, 14, P.shellLine); r(8, 23, 16, 1, P.shellLine);
      break;
    case T.LOFT_SHADOW:
      for (let y = 0; y < 12; y++) r(0, y, TILE, 1, `rgba(20,16,10,${(0.32 * (12 - y)) / 12})`);
      break;
    default:
      break; // NONE：透明
  }
}

/** `inn-tiles`：20 帧一行（帧序 = INN_TILE）。已存在（正式资源）则不动。 */
export function ensureInnTileset(scene: Phaser.Scene): void {
  const ids = Object.values(INN_TILE);
  const count = Math.max(...ids) + 1;
  withCanvas(scene, INN_TILESET, count * TILE, TILE, (c) => {
    const rng = new SeededRng('inn-dev-tiles');
    for (let id = 0; id < count; id++) drawTile(c, id, id * TILE, rng);
  });
}

// ------------------------------------------------------------------ props (origin 0.5,1 at the base)
function drawColumn(scene: Phaser.Scene): void {
  withCanvas(scene, INN_PROP.column, 32, 64, (c) => {
    rect(c, 8, 0, 16, 8, P.shellFace); rect(c, 10, 8, 12, 50, P.plankDark); rect(c, 10, 8, 2, 50, P.plank); rect(c, 20, 8, 2, 50, P.woodDark);
    for (const y of [22, 25, 28]) rect(c, 9, y, 14, 2, P.straw); rect(c, 12, 30, 2, 6, P.strawDark);
    rect(c, 7, 58, 18, 6, P.grout); rect(c, 7, 58, 18, 1, '#a09888');
  });
}
function drawTurtle(scene: Phaser.Scene): void {
  withCanvas(scene, INN_PROP.turtle, 32, 32, (c) => {
    rect(c, 6, 14, 20, 12, P.turtle); rect(c, 8, 12, 16, 2, P.turtle); rect(c, 8, 26, 16, 2, P.turtleLine);
    rect(c, 10, 15, 12, 8, P.turtleHi); rect(c, 15, 15, 2, 8, P.turtleLine); rect(c, 10, 18, 12, 1, P.turtleLine);
    rect(c, 2, 18, 6, 5, P.turtleSkin); rect(c, 3, 19, 1, 1, P.ink); // 头
    rect(c, 6, 26, 5, 3, P.turtleSkin); rect(c, 21, 26, 5, 3, P.turtleSkin); rect(c, 26, 20, 3, 3, P.turtleSkin);
  });
}
function drawTable(scene: Phaser.Scene): void {
  withCanvas(scene, INN_PROP.table, 32, 32, (c) => {
    rect(c, 3, 20, 3, 12, P.woodDark); rect(c, 26, 20, 3, 12, P.woodDark);
    rect(c, 1, 4, 30, 16, P.lacquer); rect(c, 2, 5, 28, 14, P.plank); rect(c, 2, 12, 28, 1, P.plankDark);
    rect(c, 1, 20, 30, 6, P.wood); rect(c, 1, 25, 30, 1, P.woodDark);
    rect(c, 6, 8, 5, 4, P.cloth); rect(c, 20, 7, 6, 6, P.plankDark); rect(c, 22, 6, 2, 1, P.woodDark); rect(c, 14, 10, 4, 3, P.cloth);
  });
}
function drawStool(scene: Phaser.Scene): void {
  withCanvas(scene, INN_PROP.stool, 16, 16, (c) => {
    rect(c, 2, 4, 12, 5, P.plank); rect(c, 2, 8, 12, 1, P.plankDark); rect(c, 3, 9, 2, 7, P.woodDark); rect(c, 11, 9, 2, 7, P.woodDark);
  });
}
function drawJar(scene: Phaser.Scene): void {
  withCanvas(scene, INN_PROP.jar, 24, 28, (c) => {
    rect(c, 4, 6, 16, 20, P.woodDark); rect(c, 5, 7, 14, 18, P.plankDark); rect(c, 3, 10, 18, 10, P.plankDark); rect(c, 7, 9, 3, 8, '#a06a3c');
    rect(c, 7, 2, 10, 5, P.stopper); rect(c, 8, 1, 8, 1, P.strawDark); rect(c, 4, 25, 16, 2, P.shellShadow);
  });
}
function drawBarrel(scene: Phaser.Scene): void {
  withCanvas(scene, INN_PROP.barrel, 28, 30, (c) => {
    rect(c, 3, 2, 22, 26, P.woodDark); rect(c, 4, 3, 20, 24, P.plankDark); rect(c, 2, 8, 24, 14, P.plankDark);
    rect(c, 2, 7, 24, 2, P.storm); rect(c, 2, 20, 24, 2, P.storm); rect(c, 8, 4, 2, 22, P.plank); rect(c, 16, 4, 2, 22, P.plank); rect(c, 4, 27, 20, 2, P.shellShadow);
  });
}
function drawLantern(scene: Phaser.Scene): void {
  withCanvas(scene, INN_PROP.lantern, 32, 64, (c) => {
    rect(c, 22, 0, 2, 8, P.woodDark); rect(c, 22, 6, 6, 2, P.woodDark); rect(c, 26, 6, 2, 6, P.woodDark);
    rect(c, 20, 12, 14, 2, P.lacquer); rect(c, 19, 14, 16, 16, P.lacquer); rect(c, 21, 15, 12, 14, P.lantern); rect(c, 24, 18, 6, 8, P.lanternHi);
    rect(c, 20, 30, 14, 2, P.lacquer); rect(c, 26, 32, 2, 5, P.lacquer);
  });
}

/** 所有道具占位（缺失的才画）。 */
export function ensureInnProps(scene: Phaser.Scene): void {
  drawColumn(scene); drawTurtle(scene); drawTable(scene); drawStool(scene); drawJar(scene); drawBarrel(scene); drawLantern(scene);
}

// ------------------------------------------------------------------ emotes (16×16, origin bottom-centre as drawn by Actor)
const EMOTES: Record<string, (c: Ctx) => void> = {
  laugh: (c) => { rect(c, 4, 5, 3, 1, P.ink); rect(c, 9, 5, 3, 1, P.ink); rect(c, 4, 4, 1, 1, P.ink); rect(c, 6, 4, 1, 1, P.ink); rect(c, 9, 4, 1, 1, P.ink); rect(c, 11, 4, 1, 1, P.ink); rect(c, 5, 8, 6, 4, P.ink); rect(c, 6, 9, 4, 2, P.red); },
  flushed: (c) => { rect(c, 5, 5, 2, 2, P.ink); rect(c, 9, 5, 2, 2, P.ink); rect(c, 3, 8, 3, 2, P.blush); rect(c, 10, 8, 3, 2, P.blush); rect(c, 6, 11, 4, 1, P.ink); },
  dim: (c) => { rect(c, 4, 6, 3, 1, P.grey); rect(c, 9, 6, 3, 1, P.grey); rect(c, 6, 11, 4, 1, P.grey); rect(c, 3, 3, 10, 1, P.storm); },
  smug: (c) => { rect(c, 4, 4, 3, 1, P.ink); rect(c, 5, 6, 2, 1, P.ink); rect(c, 9, 5, 2, 2, P.ink); rect(c, 6, 10, 3, 1, P.ink); rect(c, 9, 9, 2, 1, P.ink); rect(c, 11, 8, 1, 1, P.ink); },
  unease: (c) => { rect(c, 5, 5, 2, 2, P.ink); rect(c, 9, 5, 2, 2, P.ink); rect(c, 5, 10, 2, 1, P.ink); rect(c, 7, 11, 2, 1, P.ink); rect(c, 9, 10, 2, 1, P.ink); rect(c, 12, 2, 1, 1, P.teal); rect(c, 11, 3, 3, 2, P.teal); rect(c, 12, 5, 1, 1, P.teal); },
  frown: (c) => { rect(c, 3, 3, 1, 1, P.ink); rect(c, 4, 4, 2, 1, P.ink); rect(c, 12, 3, 1, 1, P.ink); rect(c, 10, 4, 2, 1, P.ink); rect(c, 5, 6, 2, 2, P.ink); rect(c, 9, 6, 2, 2, P.ink); rect(c, 6, 10, 4, 1, P.ink); rect(c, 5, 11, 1, 1, P.ink); rect(c, 10, 11, 1, 1, P.ink); },
  drink: (c) => { rect(c, 5, 4, 6, 8, P.plankDark); rect(c, 6, 5, 4, 3, P.cloth); rect(c, 11, 6, 2, 4, P.plankDark); rect(c, 5, 12, 6, 1, P.woodDark); },
  angry: (c) => { rect(c, 3, 4, 2, 1, P.red); rect(c, 5, 5, 1, 1, P.red); rect(c, 11, 4, 2, 1, P.red); rect(c, 10, 5, 1, 1, P.red); rect(c, 5, 7, 2, 2, P.ink); rect(c, 9, 7, 2, 2, P.ink); rect(c, 5, 11, 6, 1, P.ink); },
  shake: (c) => { rect(c, 5, 6, 2, 2, P.ink); rect(c, 9, 6, 2, 2, P.ink); rect(c, 6, 10, 4, 1, P.ink); rect(c, 2, 5, 1, 1, P.storm); rect(c, 1, 7, 1, 1, P.storm); rect(c, 13, 5, 1, 1, P.storm); rect(c, 14, 7, 1, 1, P.storm); },
  happy: (c) => { rect(c, 5, 5, 2, 2, P.ink); rect(c, 9, 5, 2, 2, P.ink); rect(c, 5, 9, 1, 1, P.ink); rect(c, 6, 10, 4, 1, P.ink); rect(c, 10, 9, 1, 1, P.ink); },
};

/** 本场景自带占位图标的表情名。 */
export const INN_DEV_EMOTE_NAMES: readonly string[] = Object.keys(EMOTES);

/** 表情气泡占位：`emote-<name>`（Actor.showEmote 按 key 优先使用）。有正式别名图（`emote-anger` 等）的名字不画。 */
export function ensureInnEmotes(scene: Phaser.Scene): void {
  for (const [name, draw] of Object.entries(EMOTES)) {
    const alias = EMOTE_ICON_ALIAS[name];
    if (alias && scene.textures.exists(`emote-${alias}`)) continue;
    withCanvas(scene, `emote-${name}`, 16, 16, (c) => { rect(c, 1, 1, 14, 14, P.ink); rect(c, 2, 2, 12, 12, P.bubble); draw(c); });
  }
}

/**
 * DEV ONLY：public/assets/dev 的角色图集没有坐姿帧，坐在桌边的人会站着。给已加载的开发图集补上
 * `sit-<dir>-0` 别名帧（指向 idle 帧），并让动画表重建，这样 Actor.play('sit') 至少能定住。
 * 正式图集自带 sit 帧（资产需求单），届时这里不做任何事。
 */
export function ensureDevSitFrames(scene: Phaser.Scene, actorKeys: readonly string[]): void {
  for (const key of actorKeys) {
    const def = ACTOR_DEFS[key];
    const atlas = def?.devAtlas;
    if (!atlas || scene.textures.exists(def.texture) || !scene.textures.exists(atlas)) continue;
    const tex = scene.textures.get(atlas);
    if (tex.has(frameName('sit', 'down', 0))) continue;
    let added = false;
    for (const facing of FACINGS) {
      const idle = frameName('idle', facing, 0);
      if (!tex.has(idle)) continue;
      const src = tex.get(idle);
      tex.add(frameName('sit', facing, 0), 0, src.cutX, src.cutY, src.cutWidth, src.cutHeight);
      added = true;
    }
    if (added) forgetActorAnims(atlas);
  }
}

/** 一次性准备本场景需要的全部占位纹理。 */
export function ensureInnDevTextures(scene: Phaser.Scene): void {
  ensureInnTileset(scene);
  ensureInnProps(scene);
  ensureInnEmotes(scene);
  ensureDevSitFrames(scene, ['luo-dahu', 'wu-yong', 'jiang-zichao', 'zhao-chengbo']);
}
